import { ElementRef, Injectable, OnDestroy, ViewChild } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { GeneralRequestService } from "./general-request.service";
import { VideoMeeting } from "../models/video-meeting.model";
import { NotificationService } from "./notification.service";
import { Router } from "@angular/router";
import { DeviceDetectService } from "./device-detect.service";
import { UrlService } from "./url.service";
import { getPathWithoutQueryAndHash } from "../shared/utils/url.utils";

declare const JitsiMeetExternalAPI: any;

@Injectable({
  providedIn: 'root'
})
export class VideoService implements OnDestroy {
  private readonly preMeetingWindowMs = 5 * 60 * 1000;
  private readonly activeMeetingWindowMs = 30 * 60 * 1000;
  videoMeetingsSub!: Subscription;
  isShowingMeetings!: boolean;
  meetingClosed = new Subject<any>();
  meetingClosedSubscription!: Subscription;
  notificationMeetingSub!: Subscription;
  roomCode!: string;
  @ViewChild("videoMeetingContainer") videoMeetingContainer!: ElementRef;
  timeNow!: number;
  filteredVideoMeetings: VideoMeeting[] = [];
  openMeetingIds: number[] = [];
  meetingStatuses: { pending: number; cancelled: number; done: number; ongoing: number; } = {
    pending: null!,
    cancelled: null!,
    done: null!,
    ongoing: null!,
  };
  isMobile = false;
  videoPageSub!: Subscription;
  isVideoPage = false;

  constructor(
    private generalRequestService: GeneralRequestService,
    public notificationService: NotificationService,
    public router: Router,
    private deviceDetect: DeviceDetectService,
    private urlService: UrlService,
  ) {
    this.isMobile = this.deviceDetect.isMobile;
    this.getMeetingStatuses();

    this.videoPageSub = this.urlService.currentUrl$.subscribe((url) => {
      this.isVideoPage = url === "/video";
    });
  }

  initiate(): void {
    this.initInListMode();
  }

  getMeetingStatuses() {
    this.meetingStatuses = {
      pending: parseInt(sessionStorage.getItem("MEETING_STATUS_PENDING")!),
      cancelled: parseInt(sessionStorage.getItem("MEETING_STATUS_CANCELLED")!),
      done: parseInt(sessionStorage.getItem("MEETING_STATUS_DONE")!),
      ongoing: parseInt(sessionStorage.getItem("MEETING_STATUS_ONGOING")!),
    };
  }

  subscribeToVideoMeetings() {
    this.videoMeetingsSub = this.generalRequestService
      .getVideoMeetings()
      .subscribe((meetings) => {
        this.timeNow = Date.now();
        this.filteredVideoMeetings = meetings ? [...meetings] : [];
      });
  }

  compileDoctorName(meeting: VideoMeeting) {
    const first = meeting.doctor.person.firstName;
    const last = meeting.doctor.person.lastName;
    return (
      first.substr(0, 1) +
      first.substring(1).toLowerCase() +
      " " +
      last.substr(0, 1) +
      last.substring(1).toLowerCase()
    );
  }

  toggleMeeting(meeting: VideoMeeting) {
    if (this.openMeetingIds.includes(meeting.id)) {
      this.openMeetingIds.splice(this.openMeetingIds.indexOf(meeting.id), 1);
    } else {
      this.openMeetingIds.push(meeting.id);
    }
  }

  /**
   * Unified rule for showing video-visit notifications across nav and menu surfaces.
   * Meeting is actionable only from 5 min before until 30 min after start.
   */
  isMeetingActionableForNotification(videoMeeting: Pick<VideoMeeting, "statusId" | "meetingTime">): boolean {
    const now = Date.now();
    const isActionableStatus = [this.meetingStatuses.pending, this.meetingStatuses.ongoing].includes(videoMeeting.statusId);
    if (!isActionableStatus || !videoMeeting.meetingTime) {
      return false;
    }
    return (
      now >= videoMeeting.meetingTime - this.preMeetingWindowMs &&
      now < videoMeeting.meetingTime + this.activeMeetingWindowMs
    );
  }

  isMeetingForPlatform(
    videoMeeting: Pick<VideoMeeting, "registerId">,
    premiumRegisterIds: number[] | null | undefined,
    isPaidPlatform: boolean,
  ): boolean {

    if (premiumRegisterIds == null) {
      return false;
    }

    if (premiumRegisterIds.length === 0) {
      return !isPaidPlatform;
    }

    const isPaidMeeting = premiumRegisterIds.includes(videoMeeting.registerId);
    return isPaidPlatform ? isPaidMeeting : !isPaidMeeting;
  }

  isMeetingVisibleForPlatform(
    videoMeeting: Pick<VideoMeeting, "statusId" | "meetingTime" | "registerId">,
    premiumRegisterIds: number[] | null | undefined,
    isPaidPlatform: boolean,
  ): boolean {
    return this.isMeetingForPlatform(videoMeeting, premiumRegisterIds, isPaidPlatform)
      && this.isMeetingActionableForNotification(videoMeeting);
  }

  getVisibleMeetingsCountForPlatform(
    meetings: Array<Pick<VideoMeeting, "statusId" | "meetingTime" | "registerId">>,
    premiumRegisterIds: number[] | null | undefined,
    isPaidPlatform: boolean,
  ): number {
    if (premiumRegisterIds == null) {
      return 0;
    }
    return meetings.filter((meeting) =>
      this.isMeetingVisibleForPlatform(meeting, premiumRegisterIds, isPaidPlatform)
    ).length;
  }

  /**
   * Helper method to retrieve and format the user's name from session storage.
   */
  getFormattedUserName(): string {
    const sessionContext = JSON.parse(sessionStorage.getItem("sessionContext")!);
    if (!sessionContext || !sessionContext.principal) {
      return "Guest";
    }
    return `${sessionContext.principal.firstName} ${sessionContext.principal.lastName}`;
  }

  changeMeetingStatus(meeting: VideoMeeting) {
    if (!meeting) {
      return;
    }
    const payload = {
      items: [
        {
          type: "meeting",
          id: meeting.id,
          statusId: +sessionStorage.getItem("MEETING_STATUS_ONGOING")!,
        },
      ],
      mode: "SAVE_PARTIAL",
    };
    this.generalRequestService.postRequest(payload);
  }

  showVideoCallView() {
    if (!this.isVideoPage || getPathWithoutQueryAndHash(this.router.url) !== '/video') return;
    this.isShowingMeetings = false;
  }

  showMeetingListView() {
    if (!this.isVideoPage || getPathWithoutQueryAndHash(this.router.url) !== '/video') return;
    this.isShowingMeetings = true;
  }

  ngOnDestroy() {
    if (this.videoMeetingsSub) {
      this.videoMeetingsSub.unsubscribe();
    }
    if (this.notificationMeetingSub) {
      this.notificationMeetingSub.unsubscribe();
    }
    if (this.meetingClosedSubscription) {
      this.meetingClosedSubscription.unsubscribe();
    }
    if (this.videoPageSub) {
      this.videoPageSub.unsubscribe();
    }
  }

  private initInListMode() {
    this.showMeetingListView();
    this.generalRequestService.fetchMeetings();
    this.subscribeToVideoMeetings();
    this.meetingClosedSubscription = this.meetingClosed.subscribe(() => {
      this.showMeetingListView();
    });
  }
}
